import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Order, Shipment } from "@/api.types.v2";
import { logger } from "@/services/logger";
import type { OrderDraft } from "./types";

const LEGACY_DRAFT_KEY = "ezx.create-order-draft.v1";
const DRAFT_KEY_PREFIX = "ezx.create-order-draft.v2:";
const SAVE_DEBOUNCE_MS = 600;

interface CreateOrderContextValue {
  data: OrderDraft;
  update: (patch: Partial<OrderDraft>) => void;
  reset: () => void;
  persistDraft: () => boolean;
  /** Clear the persisted draft + in-memory data without touching the
   * created-order state. Called after a successful order placement so
   * the confirmation screen can still read createdOrder. */
  discardDraft: () => void;
  createdOrder: Order | null;
  setCreatedOrder: (order: Order | null) => void;
  createdShipments: Shipment[];
  setCreatedShipments: (shipments: Shipment[]) => void;
  /** ms-since-epoch of the last successful sessionStorage write, or null. */
  lastSavedAt: number | null;
}

const CreateOrderContext = createContext<CreateOrderContextValue | null>(null);

function draftKey(customerId: string) {
  return `${DRAFT_KEY_PREFIX}${encodeURIComponent(customerId)}`;
}

function loadDraft(customerId: string | null): OrderDraft {
  if (typeof sessionStorage === "undefined") return {};
  try {
    if (!customerId) return {};
    const raw = sessionStorage.getItem(draftKey(customerId));
    if (!raw) return {};
    return JSON.parse(raw) as OrderDraft;
  } catch (e) {
    logger.error("Failed to read order draft from sessionStorage", e);
    return {};
  }
}

function clearDraft(key: string) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    logger.error("Failed to clear order draft", e);
  }
}

interface CreateOrderProviderProps {
  customerId: string | null;
  children: ReactNode;
}

export function CreateOrderProvider(props: CreateOrderProviderProps) {
  // Reset memory synchronously when identity changes, before children render.
  return (
    <CustomerDraftProvider key={props.customerId ?? undefined} {...props} />
  );
}

function CustomerDraftProvider({
  customerId,
  children,
}: CreateOrderProviderProps) {
  const active = useRef(true);
  useLayoutEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  // Never hydrate an old shared entry: its owner cannot be established.
  useEffect(() => clearDraft(LEGACY_DRAFT_KEY), []);
  const [data, setData] = useState<OrderDraft>(() => loadDraft(customerId));
  const [createdOrder, setCreatedOrder] = useState<Order | null>(null);
  const [createdShipments, setCreatedShipments] = useState<Shipment[]>([]);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(() =>
    Object.keys(data).length > 0 ? Date.now() : null
  );

  const persistDraft = useCallback(() => {
    if (!customerId || !active.current) return false;
    try {
      sessionStorage.setItem(draftKey(customerId), JSON.stringify(data));
      setLastSavedAt(Date.now());
      return true;
    } catch (error) {
      logger.error("Failed to persist order draft", error);
      return false;
    }
  }, [customerId, data]);

  useEffect(() => {
    if (!customerId || Object.keys(data).length === 0) return;
    const timer = window.setTimeout(persistDraft, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [customerId, data, persistDraft]);

  const update = (patch: Partial<OrderDraft>) => {
    if (!customerId || !active.current) return;
    setData((prev) => ({ ...prev, ...patch }));
  };

  const discardDraft = () => {
    if (!customerId || !active.current) return;
    setData({});
    setLastSavedAt(null);
    clearDraft(draftKey(customerId));
  };

  const reset = () => {
    discardDraft();
    setCreatedOrder(null);
    setCreatedShipments([]);
  };

  return (
    <CreateOrderContext.Provider
      value={{
        data,
        update,
        reset,
        persistDraft,
        discardDraft,
        createdOrder,
        setCreatedOrder,
        createdShipments,
        setCreatedShipments,
        lastSavedAt,
      }}
    >
      {children}
    </CreateOrderContext.Provider>
  );
}

export function useCreateOrder() {
  const ctx = useContext(CreateOrderContext);
  if (!ctx)
    throw new Error("useCreateOrder must be used inside CreateOrderProvider");
  return ctx;
}
